import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import axiosClient from '@/api/axios-client';
import PasswordInput from '@/components/dataEntry/PasswordInput';
import Button from '@/components/general/Button';
import { useStateContext } from '@/contexts/ContextProvider';

interface ChangePasswordFormProps {
    onSuccess?: () => void;
    onCancel?: () => void;
}

/**
 * Changement de SON mot de passe : ancien + nouveau saisi deux fois. Le serveur
 * ferme les autres sessions et prévient le titulaire par e-mail.
 */
export default function ChangePasswordForm({ onSuccess, onCancel }: ChangePasswordFormProps) {
    const { t } = useTranslation('auth');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [errors, setErrors] = useState<Record<string, string[]>>({});
    const { setNotifications, user, setUser } = useStateContext();
    const queryClient = useQueryClient();

    const mutation = useMutation(
        async () => {
            const { data } = await axiosClient.patch('/users/me/change-password', {
                current_password: currentPassword,
                new_password: newPassword,
                new_password_confirmation: confirmation,
            });
            return data;
        },
        {
            onSuccess: (data: any) => {
                setNotifications({ success: t('change_password.success') });
                // Also update the in-memory user so `must_change_password` flips to false
                // and gating screens (PasswordProfileStep, MustChangePasswordModal) update.
                if (setUser && data) {
                    // Backend returns either { data: {...user fields...} } or the raw user object
                    const updatedUser = data?.data ?? data;
                    setUser({ ...(user as any), ...updatedUser });
                }
                queryClient.invalidateQueries(['/users/me']);
                setCurrentPassword('');
                setNewPassword('');
                setConfirmation('');
                setErrors({});
                onSuccess?.();
            },
            onError: (error: any) => {
                const responseErrors = error?.response?.data?.errors ?? {};
                setErrors(responseErrors);
            },
        },
    );

    const isMismatch = confirmation !== '' && confirmation !== newPassword;
    const isDisabled = !currentPassword || !newPassword || !confirmation || isMismatch || mutation.isLoading;

    return (
        <div className="flex flex-col gap-4">
            <div>
                <PasswordInput
                    id="current_password"
                    label={t('change_password.current')}
                    value={currentPassword}
                    onchange={(e: any) => setCurrentPassword(e.target.value)}
                    error={!!errors.current_password}
                    attributes={{ autoComplete: 'current-password' }}
                    required
                />
                {errors.current_password?.[0] && (
                    <p className="text-red-500 text-xs mt-1">{errors.current_password[0]}</p>
                )}
            </div>
            <div>
                <PasswordInput
                    id="new_password"
                    showRules
                    label={t('change_password.new')}
                    value={newPassword}
                    onchange={(e: any) => setNewPassword(e.target.value)}
                    error={!!errors.new_password}
                    attributes={{ autoComplete: 'new-password' }}
                    required
                />
                {errors.new_password?.[0] && (
                    <p className="text-red-500 text-xs mt-1">{errors.new_password[0]}</p>
                )}
            </div>
            <div>
                <PasswordInput
                    id="new_password_confirmation"
                    label={t('change_password.confirm')}
                    value={confirmation}
                    onchange={(e: any) => setConfirmation(e.target.value)}
                    error={isMismatch}
                    attributes={{ autoComplete: 'new-password' }}
                    required
                />
                {isMismatch && (
                    <p className="text-red-500 text-xs mt-1">{t('change_password.mismatch')}</p>
                )}
            </div>
            <div className="flex justify-end gap-3">
                {onCancel && (
                    <Button type="default" elementType="button" attributes={{ onClick: onCancel, type: 'button' }}>
                        {t('change_password.cancel')}
                    </Button>
                )}
                <Button
                    type="primary"
                    elementType="button"
                    attributes={{ onClick: () => mutation.mutate(), type: 'button' }}
                    disabled={isDisabled}
                >
                    {mutation.isLoading ? t('change_password.submitting') : t('change_password.submit')}
                </Button>
            </div>
        </div>
    );
}
